/**
 * Next.js Email/Password Authentication Routes
 * Provides pre-built API routes for email-password login, registration, and password reset
 *
 * Usage:
 * ```typescript
 * import { getEmailAuthRoutes } from '@kitiumai/auth'
 * const { createRegisterRoute } = await getEmailAuthRoutes()
 * export const POST = createRegisterRoute(storage, config)
 * ```
 */

import { StorageAdapter } from '../types'
import { IEmailProvider } from '../email/types'
import { validateEmail, normalizeEmail, validatePasswordStrength } from '../password'
import * as argon2 from 'argon2'
import { nanoid } from 'nanoid'

interface NextJSRequest {
  json(): Promise<any>
  url: string
  method: string
  headers: Record<string, string>
}

interface NextJSResponse {
  status(code: number): NextJSResponse
  json(data: any): NextJSResponse
}

export interface EmailAuthRoutesConfig {
  appUrl: string
  emailProvider: IEmailProvider
  loginRedirect?: string
  registerRedirect?: string
  // Validation rules
  minNameLength?: number
  maxNameLength?: number
  // Session configuration
  sessionTtlSeconds?: number
  // Password reset configuration
  passwordResetTokenTtlSeconds?: number
  passwordResetTokenLength?: number
}

/**
 * Register route handler
 * POST /api/auth/register
 */
export function createRegisterRoute(storage: StorageAdapter, config: EmailAuthRoutesConfig) {
  return async (req: NextJSRequest, res: NextJSResponse) => {
    if (req.method !== 'POST') {
      return res.status(405).json({ error: 'Method not allowed' })
    }

    try {
      const { name, email, password, confirmPassword } = await req.json()

      // Validate required fields
      if (!name || !email || !password || !confirmPassword) {
        return res.status(400).json({ error: 'Missing required fields' })
      }

      // Validate name
      const minNameLength = config.minNameLength ?? 2
      const maxNameLength = config.maxNameLength ?? 255
      if (typeof name !== 'string' || name.trim().length < minNameLength) {
        return res.status(400).json({ error: `Name must be at least ${minNameLength} characters` })
      }
      if (name.trim().length > maxNameLength) {
        return res.status(400).json({ error: `Name must be at most ${maxNameLength} characters` })
      }

      // Validate email
      const normalizedEmail = normalizeEmail(email)
      if (!validateEmail(normalizedEmail)) {
        return res.status(400).json({ error: 'Invalid email address' })
      }

      // Validate password
      const passwordValidation = validatePasswordStrength(password)
      if (!passwordValidation.valid) {
        return res.status(400).json({
          error: 'Password does not meet requirements',
          details: passwordValidation.errors
        })
      }

      // Verify passwords match
      if (password !== confirmPassword) {
        return res.status(400).json({ error: 'Passwords do not match' })
      }

      // Check if user already exists
      const existingUser = await storage.getUserByEmail(normalizedEmail)
      if (existingUser) {
        return res.status(409).json({ error: 'Email already registered' })
      }

      // Hash password
      const passwordHash = await argon2.hash(password)

      // Create user
      const user = await storage.createUser({
        name: name.trim(),
        email: normalizedEmail,
        metadata: {
          passwordHash,
          emailVerified: false
        }
      })

      // Create session
      const sessionTtlSeconds = config.sessionTtlSeconds ?? 7 * 24 * 60 * 60 // 7 days
      const expiresAt = new Date(Date.now() + sessionTtlSeconds * 1000)
      const session = await storage.createSession({
        userId: user.id,
        entitlements: user.entitlements || [],
        expiresAt,
        metadata: { authMethod: 'email' },
        updatedAt: new Date()
      })

      return res.status(201).json({
        success: true,
        user: {
          id: user.id,
          email: user.email,
          name: user.name
        },
        sessionId: session.id,
        redirectUrl: config.registerRedirect || `${config.appUrl}/onboarding`
      })
    } catch (error) {
      console.error('Registration error:', error)
      return res.status(500).json({ error: 'Failed to register' })
    }
  }
}

/**
 * Login route handler
 * POST /api/auth/login
 */
export function createLoginRoute(storage: StorageAdapter, config: EmailAuthRoutesConfig) {
  return async (req: NextJSRequest, res: NextJSResponse) => {
    if (req.method !== 'POST') {
      return res.status(405).json({ error: 'Method not allowed' })
    }

    try {
      const { email, password } = await req.json()

      // Validate required fields
      if (!email || !password) {
        return res.status(400).json({ error: 'Email and password required' })
      }

      // Normalize and validate email
      const normalizedEmail = normalizeEmail(email)
      if (!validateEmail(normalizedEmail)) {
        return res.status(400).json({ error: 'Invalid email address' })
      }

      // Find user
      const user = await storage.getUserByEmail(normalizedEmail)
      if (!user) {
        return res.status(401).json({ error: 'Invalid email or password' })
      }

      // Check if user has password set
      const passwordHash = user.metadata?.passwordHash
      if (!passwordHash) {
        return res.status(401).json({
          error: 'This account uses social login. Please sign in with the original provider'
        })
      }

      // Verify password
      const isPasswordValid = await argon2.verify(passwordHash, password)
      if (!isPasswordValid) {
        return res.status(401).json({ error: 'Invalid email or password' })
      }

      // Create session
      const sessionTtlSeconds = config.sessionTtlSeconds ?? 7 * 24 * 60 * 60 // 7 days
      const expiresAt = new Date(Date.now() + sessionTtlSeconds * 1000)
      const session = await storage.createSession({
        userId: user.id,
        entitlements: user.entitlements || [],
        expiresAt,
        metadata: { authMethod: 'email' },
        updatedAt: new Date()
      })

      return res.status(200).json({
        success: true,
        user: {
          id: user.id,
          email: user.email,
          name: user.name
        },
        sessionId: session.id,
        redirectUrl: config.loginRedirect || `${config.appUrl}/dashboard`
      })
    } catch (error) {
      console.error('Login error:', error)
      return res.status(500).json({ error: 'Failed to login' })
    }
  }
}

/**
 * Forgot password route handler
 * POST /api/auth/forgot-password
 */
export function createForgotPasswordRoute(storage: StorageAdapter, config: EmailAuthRoutesConfig) {
  return async (req: NextJSRequest, res: NextJSResponse) => {
    if (req.method !== 'POST') {
      return res.status(405).json({ error: 'Method not allowed' })
    }

    try {
      const { email } = await req.json()

      // Validate email
      if (!email) {
        return res.status(400).json({ error: 'Email required' })
      }

      const normalizedEmail = normalizeEmail(email)
      if (!validateEmail(normalizedEmail)) {
        return res.status(400).json({ error: 'Invalid email address' })
      }

      // Find user
      const user = await storage.getUserByEmail(normalizedEmail)
      if (!user) {
        // Don't reveal if user exists (security best practice)
        return res.status(200).json({
          success: true,
          message: 'If an account exists, a password reset link will be sent'
        })
      }

      // Check if user has password-based auth
      if (!user.metadata?.passwordHash) {
        return res.status(200).json({
          success: true,
          message: 'If an account exists, a password reset link will be sent'
        })
      }

      // Generate reset token
      const tokenLength = config.passwordResetTokenLength ?? 32
      const resetToken = nanoid(tokenLength)
      const resetTokenHash = await argon2.hash(resetToken)

      // Store token in user metadata
      const resetTokenTtlSeconds = config.passwordResetTokenTtlSeconds ?? 60 * 60 // 1 hour
      await storage.updateUser(user.id, {
        metadata: {
          ...user.metadata,
          passwordResetToken: resetTokenHash,
          passwordResetTokenExpiry: new Date(Date.now() + resetTokenTtlSeconds * 1000)
        }
      })

      // Send reset email
      const resetLink = `${config.appUrl}/reset-password?token=${resetToken}&email=${encodeURIComponent(normalizedEmail)}`
      await config.emailProvider.sendPasswordResetLink(normalizedEmail, resetLink)

      return res.status(200).json({
        success: true,
        message: 'Password reset link sent to email'
      })
    } catch (error) {
      console.error('Forgot password error:', error)
      return res.status(500).json({ error: 'Failed to process request' })
    }
  }
}

/**
 * Reset password route handler
 * POST /api/auth/reset-password
 */
export function createResetPasswordRoute(storage: StorageAdapter, _config: EmailAuthRoutesConfig) {
  return async (req: NextJSRequest, res: NextJSResponse) => {
    if (req.method !== 'POST') {
      return res.status(405).json({ error: 'Method not allowed' })
    }

    try {
      const { token, email, password, confirmPassword } = await req.json()

      // Validate required fields
      if (!token || !email || !password || !confirmPassword) {
        return res.status(400).json({ error: 'Missing required fields' })
      }

      // Normalize email
      const normalizedEmail = normalizeEmail(email)
      if (!validateEmail(normalizedEmail)) {
        return res.status(400).json({ error: 'Invalid email address' })
      }

      // Validate new password
      const passwordValidation = validatePasswordStrength(password)
      if (!passwordValidation.valid) {
        return res.status(400).json({
          error: 'Password does not meet requirements',
          details: passwordValidation.errors
        })
      }

      // Verify passwords match
      if (password !== confirmPassword) {
        return res.status(400).json({ error: 'Passwords do not match' })
      }

      // Find user
      const user = await storage.getUserByEmail(normalizedEmail)
      if (!user) {
        return res.status(400).json({ error: 'Invalid reset link' })
      }

      // Verify reset token
      const storedToken = user.metadata?.passwordResetToken
      const tokenExpiry = user.metadata?.passwordResetTokenExpiry
      if (!storedToken || !tokenExpiry) {
        return res.status(400).json({ error: 'Invalid or expired reset link' })
      }

      if (new Date() > new Date(tokenExpiry)) {
        return res.status(400).json({ error: 'Reset link has expired' })
      }

      const isTokenValid = await argon2.verify(storedToken, token)
      if (!isTokenValid) {
        return res.status(400).json({ error: 'Invalid reset link' })
      }

      // Hash new password
      const newPasswordHash = await argon2.hash(password)

      // Update user password and clear reset token
      await storage.updateUser(user.id, {
        metadata: {
          ...user.metadata,
          passwordHash: newPasswordHash,
          passwordResetToken: undefined,
          passwordResetTokenExpiry: undefined
        }
      })

      return res.status(200).json({
        success: true,
        message: 'Password reset successfully'
      })
    } catch (error) {
      console.error('Reset password error:', error)
      return res.status(500).json({ error: 'Failed to reset password' })
    }
  }
}

/**
 * Create all email auth routes
 */
export async function createEmailAuthRoutes(storage: StorageAdapter, config: EmailAuthRoutesConfig) {
  return {
    register: createRegisterRoute(storage, config),
    login: createLoginRoute(storage, config),
    forgotPassword: createForgotPasswordRoute(storage, config),
    resetPassword: createResetPasswordRoute(storage, config)
  }
}
